import { Injectable, OnDestroy, inject, signal, NgZone } from '@angular/core';
import { Observable, Subject, filter, map } from 'rxjs';
import { AppConfig } from '@env/app-config';

export interface RealTimeEvent<T = unknown> {
  eventType: string;
  payload: T;
}

/**
 * Servicio de tiempo real basado en Server-Sent Events (SSE).
 *
 * Conecta a GET /api/events/stream y despacha eventos tipados.
 * Las cookies HttpOnly se envían automáticamente por el navegador.
 *
 * Uso:
 *   realTime.connect();
 *   realTime.on<TaskUpdatedPayload>('task.updated').subscribe(payload => { ... });
 *
 * <p><b>Reconexión sin pérdida (M17.3 RF-022).</b> El `EventSource` del navegador reconecta solo,
 * pero empezaba de cero: los avisos ocurridos durante la caída no llegaban nunca — quedaban en
 * la base y aparecían al recargar la página, así que una pestaña abierta se quedaba muda. Ahora
 * el servicio recuerda el id del último aviso recibido y lo manda al reconectar, y el servidor
 * reenvía lo que falte.
 *
 * <p>La reconexión es propia y no la del navegador: aquélla reintenta a intervalo fijo y no
 * permite cambiar la URL, que es justamente lo que hace falta para mover el cursor.
 *
 * <p>Llamar disconnect() al destruir el componente raíz si se quiere cerrar manualmente.
 */
@Injectable({ providedIn: 'root' })
export class RealTimeService implements OnDestroy {
  private readonly zone = inject(NgZone);

  private eventSource: EventSource | null = null;
  private readonly events$ = new Subject<RealTimeEvent>();

  /**
   * El id del último aviso recibido (RF-022).
   *
   * <p>Sale del propio payload: cada notificación viaja con su id. El servidor acepta además la
   * cabecera `Last-Event-ID`, pero el registro de conexiones no emite ids de evento, así que el
   * navegador nunca la mandaría — el cursor lo lleva el cliente.
   */
  private ultimoEventoId: number | null = null;

  /** El reintento en curso, para poder cancelarlo al desconectar a propósito. */
  private reintento: ReturnType<typeof setTimeout> | null = null;

  /** Cuántas veces seguidas falló la conexión; fija la espera del siguiente intento. */
  private intentos = 0;

  /** La espera crece 1s, 2s, 4s… y se queda en 30s, como pide RF-022. */
  private static readonly ESPERA_INICIAL_MS = 1_000;
  private static readonly ESPERA_MAXIMA_MS = 30_000;

  readonly connectionState = signal<'disconnected' | 'connecting' | 'connected'>('disconnected');

  /** URL del endpoint SSE — puede ser sobrescrito en tests. */
  private readonly streamUrl = `${RealTimeService.origenDeLaApi()}/api/events/stream`;

  /**
   * El servidor al que va el stream. Con la API en otro origen (configuración `local`:
   * `apiUrl = http://localhost:5003`) hay que apuntar a él: el `EventSource` pedía la ruta relativa
   * al servidor de Angular, que no la tiene y contesta 404. Con `apiUrl` relativo (`/api`, detrás de
   * un proxy) la ruta relativa sigue siendo la correcta.
   */
  private static origenDeLaApi(): string {
    const base = AppConfig.apiUrl ?? '';
    return /^https?:\/\//i.test(base) ? base.replace(/\/+$/, '') : '';
  }

  connect(): void {
    if (this.eventSource) return;

    this.connectionState.set('connecting');
    this.eventSource = new EventSource(this.urlConCursor(), { withCredentials: true });

    this.eventSource.onopen = () => {
      this.zone.run(() => {
        this.intentos = 0;
        this.connectionState.set('connected');
      });
    };

    this.eventSource.onerror = () => {
      this.zone.run(() => {
        this.connectionState.set('disconnected');
        this.reconectarConEspera();
      });
    };

    // El backend envía eventos con nombre (event: task.updated\ndata: {...})
    // Usamos un listener genérico para todos los tipos de evento
    this.addMessageListener('task.updated');
    this.addMessageListener('task.created');
    this.addMessageListener('task.deleted');
    this.addMessageListener('space.updated');
    this.addMessageListener('space.created');
    this.addMessageListener('team.updated');
    this.addMessageListener('time-entry.created');
    this.addMessageListener('notification.new');
    this.addMessageListener('comment.created');
    this.addMessageListener('helpdesk.ticket.updated');
    this.addMessageListener('crm.deal.updated');
    // M05 — Colaboración y Comunicación
    this.addMessageListener('message.created');
    this.addMessageListener('message.updated');
    this.addMessageListener('message.deleted');
    this.addMessageListener('channel.member_joined');
    this.addMessageListener('actionitem.created');
    this.addMessageListener('actionitem.statusChanged');
    this.addMessageListener('actionitem.reassigned');
    this.addMessageListener('actionitem.dueDateExtended');
    this.addMessageListener('actionitem.promoted');
    // El procesamiento de un clip (transcodificación, miniatura, transcripción) corre en un hilo
    // de fondo minutos después de que la subida ya respondió 200 — sin este listener, la galería
    // se quedaba mostrando "Transcripción pendiente" para siempre porque nada le avisaba que el
    // clip había terminado de procesarse.
    this.addMessageListener('clip.updated');
    // M07 — Productividad y Tiempo
    this.addMessageListener('poker.updated');
    // M06.2 — Pizarras Virtuales: sync de elementos y presencia/cursor en vivo
    this.addMessageListener('board.element.created');
    this.addMessageListener('board.element.updated');
    this.addMessageListener('board.element.deleted');
    this.addMessageListener('board.presence');
    // M17 — Comunicaciones Síncronas
    this.addMessageListener('message.reaction');
    this.addMessageListener('message.read');
    this.addMessageListener('channel.typing');
    this.addMessageListener('channel.updated');
    this.addMessageListener('mention.created');
    // Presencia en línea/ausente/desconectado empujada por el servidor (INC: antes cada pestaña
    // preguntaba por polling cada 30s; ahora el backend avisa el cambio, ver
    // ChannelPresenceSweepScheduler y MessagingService.heartbeat).
    this.addMessageListener('presence.updated');
    this.addMessageListener('meeting.started');
    this.addMessageListener('meeting.ended');
    this.addMessageListener('meeting.cancelled');
    this.addMessageListener('meeting.participant_joined');
    this.addMessageListener('meeting.participant_left');
    this.addMessageListener('meeting.participant_updated');
    this.addMessageListener('meeting.recording');
    this.addMessageListener('meeting.notes_updated');
    // Videollamadas 1:1 (WebRTC): estos eventos solo transportan señalización (invitación,
    // offer/answer/ICE, fin de llamada) -- el audio/video real nunca pasa por el servidor, viaja
    // P2P directo entre los dos navegadores.
    this.addMessageListener('call.invite');
    this.addMessageListener('call.signal');
    this.addMessageListener('call.answered');
    this.addMessageListener('call.declined');
    this.addMessageListener('call.ended');
    // M25.1 — Celebraciones del Éxito: hito completado, sprint 100%, objetivo alcanzado.
    // Efímero (RN-001): no hay historial que consultar, solo esta transmisión en vivo.
    this.addMessageListener('celebration.trigger');
  }

  disconnect(): void {
    if (this.reintento) {
      clearTimeout(this.reintento);
      this.reintento = null;
    }
    this.intentos = 0;
    this.eventSource?.close();
    this.eventSource = null;
    this.connectionState.set('disconnected');
  }

  /**
   * La URL del stream con el cursor, si hay algo que retomar.
   *
   * <p>Sin cursor en la primera conexión: quien entra por primera vez no se ha perdido nada, y
   * mandar un cero haría que el servidor reenviara la bandeja entera.
   */
  private urlConCursor(): string {
    return this.ultimoEventoId === null
      ? this.streamUrl
      : `${this.streamUrl}?lastEventId=${this.ultimoEventoId}`;
  }

  /**
   * Programa el siguiente intento con espera creciente (RF-022: 1s, 2s, 4s… máx 30s).
   *
   * <p>La reconexión la hace este servicio y no el navegador porque hay que cambiar la URL para
   * mover el cursor — el `EventSource` reintenta siempre contra la misma.
   */
  private reconectarConEspera(): void {
    if (this.reintento) return;

    this.eventSource?.close();
    this.eventSource = null;

    const espera = Math.min(
      RealTimeService.ESPERA_INICIAL_MS * Math.pow(2, this.intentos),
      RealTimeService.ESPERA_MAXIMA_MS,
    );
    this.intentos++;
    // Fuera de la zona de Angular: un temporizador recurrente dentro mantendría viva la
    // detección de cambios y las pruebas nunca se estabilizarían.
    this.zone.runOutsideAngular(() => {
      this.reintento = setTimeout(() => {
        this.reintento = null;
        this.zone.run(() => this.connect());
      }, espera);
    });
  }

  /**
   * Filtra eventos por tipo y emite el payload tipado.
   *
   * @param eventType nombre del evento del servidor, ej: 'task.updated'
   */
  on<T>(eventType: string): Observable<T> {
    return this.events$.pipe(
      filter(e => e.eventType === eventType),
      map(e => e.payload as T)
    );
  }

  /** Observable de todos los eventos (para depuración o logging). */
  allEvents(): Observable<RealTimeEvent> {
    return this.events$.asObservable();
  }

  private addMessageListener(eventType: string): void {
    this.eventSource?.addEventListener(eventType, (event: MessageEvent) => {
      this.zone.run(() => {
        try {
          const payload = JSON.parse(event.data);
          this.anotarCursor(event, payload);
          this.events$.next({ eventType, payload });
        } catch {
          this.events$.next({ eventType, payload: event.data });
        }
      });
    });
  }

  /**
   * Avanza el cursor con lo que acaba de llegar.
   *
   * <p>Del `lastEventId` del evento si el servidor lo mandó —es el caso del reenvío— y, si no,
   * del id que trae el propio aviso. Sólo avanza: un evento fuera de orden no puede retroceder
   * el cursor, porque entonces la siguiente reconexión reenviaría lo ya visto.
   */
  private anotarCursor(event: MessageEvent, payload: unknown): void {
    const delEvento = Number(event.lastEventId);
    const delPayload = Number((payload as { id?: unknown })?.id);
    const id = Number.isFinite(delEvento) && delEvento > 0 ? delEvento : delPayload;
    if (!Number.isFinite(id) || id <= 0) return;
    if (this.ultimoEventoId === null || id > this.ultimoEventoId) {
      this.ultimoEventoId = id;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.events$.complete();
  }
}
